import React, { useState } from 'react'
import styled from 'styled-components'
import { X } from 'lucide-react'
import { Modal } from './Modal'
import { useAuth } from '../../contexts/AuthContext'
import { theme } from '../../theme/theme'

const ModalHeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.lg};
`

const ModalTitle = styled.h2`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.default};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`

const ModalBodyContent = styled.div`
  padding: ${theme.spacing.lg};
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const FormLabel = styled.label`
  font-size: ${theme.typography.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const FormInput = styled.input`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const PasswordRequirements = styled.div`
  font-size: ${theme.typography.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${theme.spacing.xs};
  
  ul {
    margin: ${theme.spacing.xs} 0;
    padding-left: ${theme.spacing.md};
  }
  
  li {
    margin: ${theme.spacing.xs} 0;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.transitions.default};
  border: none;
  
  ${(props) =>
    props.$variant === 'primary'
      ? `
    background-color: ${props.theme.colors.primary};
    color: #000000;
    
    &:hover:not(:disabled) {
      background-color: ${props.theme.colors.primaryDark};
    }
  `
      : `
    background-color: ${props.theme.colors.surface};
    border: 1px solid ${props.theme.colors.border};
    color: ${props.theme.colors.text};
    
    &:hover:not(:disabled) {
      background-color: ${props.theme.colors.surfaceHover};
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  color: #ff6b6b;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const SuccessMessage = styled.div`
  background-color: rgba(151, 251, 87, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

interface ChangePasswordModalProps {
  isOpen: boolean
  onClose: () => void
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const { changePassword } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const validatePasswords = () => {
    if (newPassword.length < 6) {
      return 'New password must be at least 6 characters long'
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match'
    }
    if (newPassword === currentPassword) {
      return 'New password must be different from current password'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    const validationError = validatePasswords()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)

    try {
      const { error } = await changePassword(newPassword)
      if (error) {
        setError(error.message)
      } else {
        setSuccess('Password changed successfully!')
        // Clear the form
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')

        // Close modal after a short delay
        setTimeout(() => {
          onClose()
          setSuccess(null)
        }, 1500)
      }
    } catch (err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
      // Reset form state when closing
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setError(null)
      setSuccess(null)
    }
  }

  const header = (
    <ModalHeaderContent>
      <ModalTitle>Change Password</ModalTitle>
      <CloseButton onClick={handleClose} disabled={loading}>
        <X size={20} />
      </CloseButton>
    </ModalHeaderContent>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="500px" header={header}>
      <ModalBodyContent>
        <Form onSubmit={handleSubmit}>
          <FormField>
            <FormLabel>Current Password</FormLabel>
            <FormInput
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              required
              disabled={loading || success !== null}
            />
          </FormField>

          <FormField>
            <FormLabel>New Password</FormLabel>
            <FormInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              required
              disabled={loading || success !== null}
              minLength={6}
            />
            <PasswordRequirements>
              <ul>
                <li>At least 6 characters long</li>
                <li>Should be different from your current password</li>
                <li>Use a unique and memorable password</li>
              </ul>
            </PasswordRequirements>
          </FormField>

          <FormField>
            <FormLabel>Confirm New Password</FormLabel>
            <FormInput
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              disabled={loading || success !== null}
              minLength={6}
            />
          </FormField>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <ButtonGroup>
            <Button type="button" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" $variant="primary" disabled={loading || success !== null}>
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalBodyContent>
    </Modal>
  )
}
